import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useSyncExternalStore,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EditorComponent,
  type EditorHandle,
  type EditorView,
  FileTreePanel,
} from "@diagram/editor";
import { DiagramRenderer, InputNode } from "@diagram/diagram-view";
import { useOpenProject } from "../hooks/useOpenProject";
import { useCurrentUser } from "../hooks/useCurrentUser";
import SimControls from "./SimControls";
import ReportIssueLink from "./ReportIssueLink";
import SimulationToolbar from "./SimulationToolbar";
import { CollapsiblePane } from "./CollapsiblePane";
import MetricNode from "./MetricNode";
import { MarkdownPreview } from "@diagram/markdown-view";
import { DrawOverlay } from "@diagram/draw-overlay";
import { DrawingToolbar, type DrawingState } from "./DrawingToolbar";
import { useSimulation } from "../hooks/useSimulation";
import { useMetricHistory } from "../hooks/useMetricHistory";
import { useEnrichedSpec } from "../hooks/useEnrichedSpec";
import { SIM_MODEL_EXTRA_LIBS, HELP_FILE_ENTRIES } from "../sim/ambient-types";
import { bundle } from "@diagram/sim-worker/bundle";
import { encodeEmbedPayload, type EmbedPayload } from "../embed/payload";
import EmbedDialog, { type EmbedDialogState } from "./EmbedDialog";
import {
  effectiveAppearanceTheme,
  flippedAppearanceOverride,
  type AppearanceTheme,
} from "./appearance-toggle";
import {
  JAZZ_API_KEY_STORAGE_KEY,
  JAZZ_LOCAL_ONLY_STORAGE_KEY,
} from "../jazz-api-key";
import { RelayWorker } from "../embed/RelayWorker";
import type { WorkerLike } from "@diagram/sim-worker";

/** Stable no-op unsubscribe, so useSyncExternalStore does not resubscribe. */
const NO_OP = () => {};

const NODE_TYPES = { metric: MetricNode, simInput: InputNode };
const TS_CONFIG = { module: "ESNext", moduleResolution: "Bundler" };

/**
 * Run ALL workspace sim code through the off-origin relay, not just shared
 * projects.
 *
 * The simulation evaluates user code with `new Function(...)` in the sim
 * worker (see sim-worker/src/worker.ts). For a project the current user did
 * not author (a shared/read-only diagram), that code is untrusted and must
 * not be able to read the app origin's IndexedDB/localStorage (the user's
 * Jazz data). The RelayWorker hosts the worker inside a sandboxed,
 * opaque-origin iframe where storage APIs throw, closing that hole.
 *
 * We deliberately relay EVERYTHING rather than branching on `myRole`
 * ("is this my editable project?"). Misclassifying a shared project as "mine"
 * would silently run untrusted code at the app origin — a security hole — so
 * the safe default is to never depend on getting that classification right.
 * The sim never touches storage even for own projects, so relaying is
 * functionally identical; the only cost is the iframe round-trip latency
 * (~milliseconds per snapshot, within the rAF polling budget), which keeps
 * the editor live-preview responsive.
 *
 * Module-level (referentially stable) as `useSimulation` requires.
 */
const createRelayWorker = (): WorkerLike => new RelayWorker();

// eslint-disable-next-line complexity -- large render component with many UI states
const DiagramWorkspace: React.FC = () => {
  const { diagramId } = useParams<{ diagramId: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  const { project, snapshot, isLoading } = useOpenProject(diagramId);
  const editorRef = useRef<EditorHandle | null>(null);
  // Tracked as state so that mounting the underlying CodeMirror view triggers
  // a re-render of the markdown preview, which needs the view to render its
  // comment affordances. A ref alone would never notify React that the
  // initially-null view is now available.
  const [editorView, setEditorView] = useState<EditorView | null>(null);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sim = useSimulation({ createWorker: createRelayWorker });
  const metricHistory = useMetricHistory(sim.snapshot, sim.status);

  const [activeFile, setActiveFile] = useState<string | null>("/main.ts");
  const [fileTreeCollapsed, setFileTreeCollapsed] = useState(false);
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [timeWindow, setTimeWindow] = useState<number | null>(null);
  const [forking, setForking] = useState(false);
  const [versionBannerDismissed, setVersionBannerDismissed] = useState(false);
  // Session-only viewer override for the rendering theme. `null` means
  // "respect the model author's default"; never persisted (per plan).
  const [appearanceOverride, setAppearanceOverride] =
    useState<AppearanceTheme | null>(null);
  // Whether the current load attempt exceeded the patience window. Cleared
  // in the effect's cleanup rather than its body: the body runs on every
  // render, so resetting there is the cascading-render anti-pattern, while
  // cleanup runs exactly when the attempt ends — which is the old semantics.
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  // Local-only mode can never load a shared/cloud-only document. Detect it
  // up-front so we can show rescue actions immediately instead of spinning.
  const localOnlyMode =
    typeof window !== "undefined" &&
    window.localStorage.getItem(JAZZ_LOCAL_ONLY_STORAGE_KEY) === "true";

  // For cloud-sync users we still want a fallback if the doc never arrives
  // (bad/expired API key, network issues, wrong id, etc.).
  useEffect(() => {
    if (!isLoading || !diagramId) return;
    const handle = setTimeout(() => {
      setLoadingTimedOut(true);
    }, 8000);
    return () => {
      clearTimeout(handle);
      setLoadingTimedOut(false);
    };
  }, [isLoading, diagramId]);

  const readOnlyTreeFiles = useMemo(
    () => [
      ...SIM_MODEL_EXTRA_LIBS.filter((f) => f.path.endsWith(".d.ts")),
      ...HELP_FILE_ENTRIES,
    ],
    [],
  );

  const fileStore = project?.getFileStore() ?? null;
  // Files this backend can merge character-by-character. Undefined leaves
  // the editor on the whole-document write path, which is what a read-only
  // viewer and any backend without collaborative text get.
  const collaborativeText = project?.getCollaborativeText() ?? undefined;

  const enrichedSpec = useEnrichedSpec({
    topology: sim.topology,
    metricsByNode: sim.metricsByNode,
    inputDescriptors: sim.inputDescriptors,
    inputValues: sim.inputValues,
    setInputValue: sim.setInputValue,
    metricHistory,
    timeWindow,
  });

  const compileAndBundle = useCallback(
    async (
      gateOnErrors: boolean,
    ): Promise<{ iife: string; hasErrors: boolean } | null> => {
      const editor = editorRef.current;
      if (!editor || !fileStore) return null;

      const tsFiles = fileStore.listFiles().filter((f) => f.endsWith(".ts"));
      const compiledFiles: Record<string, string> = {};
      let hasErrors = false;

      for (const path of tsFiles) {
        const result = await editor.compile(path);
        if (result.diagnostics.some((d) => d.severity === "error")) {
          hasErrors = true;
        }
        if (result.files.length > 0) {
          compiledFiles[result.files[0].path] = result.files[0].content;
        }
      }

      if (gateOnErrors && hasErrors) return { iife: "", hasErrors: true };

      const iife = await bundle(compiledFiles, "/main.js");
      return { iife, hasErrors };
    },
    [fileStore],
  );

  /** Resolves true when a fresh topology rendered. Callers that only want
   *  the side effect (the debounced live preview) can ignore the result. */
  const compileAndPreview = useCallback(async () => {
    try {
      const result = await compileAndBundle(false);
      if (result && result.iife) {
        return await sim.preview(result.iife);
      }
      return false;
    } catch {
      // Compile errors are expected during typing
      return false;
    }
  }, [sim, compileAndBundle]);

  const handleContentChange = useCallback(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => void compileAndPreview(), 500);
  }, [compileAndPreview]);

  const initialCompileDone = useRef(false);
  const handleActiveTabChange = useCallback(
    (path: string | null) => {
      setActiveFile(path);
      if (!initialCompileDone.current && path !== null) {
        initialCompileDone.current = true;
        handleContentChange();
      }
    },
    [handleContentChange],
  );

  const allReadOnlyFiles = useMemo(
    () => [...SIM_MODEL_EXTRA_LIBS, ...HELP_FILE_ENTRIES],
    [],
  );

  // Per-file read-only flag (e.g. help/README.md). The editor's CodeMirror
  // buffer still accepts edits to these files locally, but they are never
  // persisted, and the markdown preview shows the static `readOnly.content`,
  // so any comment markup written into the buffer would silently vanish on
  // re-render. Gate the comment UI accordingly.
  const activeFileIsReadOnly =
    activeFile !== null && allReadOnlyFiles.some((f) => f.path === activeFile);

  const handleFileSelect = useCallback(
    (path: string) => {
      const readOnly = allReadOnlyFiles.find((f) => f.path === path);
      editorRef.current?.openFile(path, readOnly?.content);
    },
    [allReadOnlyFiles],
  );

  const handleNavigate = useCallback(
    (targetPath: string, targetOffset: number) => {
      const readOnly = allReadOnlyFiles.find((f) => f.path === targetPath);
      editorRef.current?.openFile(targetPath, readOnly?.content, targetOffset);
    },
    [allReadOnlyFiles],
  );

  useEffect(() => {
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, []);

  // Track content of the active .md file for the right-pane preview.
  // Read-only help files supply their content inline; project files are read
  // from the FileStore and re-read whenever it emits a change event.
  // The FileStore's synchronous read is always the source of truth (see the
  // port contract in architecture.md); the subscription only bumps a counter
  // to invalidate the memo. Holding the pushed content in state instead would
  // let it shadow a newer read after switching files or forking, and deriving
  // during render keeps this out of an effect body.
  const subscribeToActiveFile = useCallback(
    (onChange: () => void) => {
      if (!activeFile || !fileStore) return NO_OP;
      return fileStore.onFileChange((path) => {
        if (path === activeFile) onChange();
      });
    },
    [activeFile, fileStore],
  );
  const activeFileContent = useSyncExternalStore(subscribeToActiveFile, () =>
    activeFile && fileStore ? (fileStore.readFile(activeFile) ?? "") : null,
  );

  const markdownSource = useMemo(() => {
    if (!activeFile || !activeFile.endsWith(".md")) return null;
    const readOnly = allReadOnlyFiles.find((f) => f.path === activeFile);
    if (readOnly) return readOnly.content;
    return activeFileContent;
  }, [activeFile, allReadOnlyFiles, activeFileContent]);

  const handleReset = useCallback(() => {
    sim.reset();
    setSpeed(1);
    void (async () => {
      // reset() keeps the previous topology so the diagram does not unmount
      // (and lose its viewport and DOM measurements) in the gap before this
      // preview replaces it. If the preview never lands — the source stopped
      // compiling since the run — the retained diagram no longer corresponds
      // to anything the user has, so drop it and let the error or empty-state
      // branch show instead.
      const rendered = await compileAndPreview();
      if (!rendered) sim.clearTopology();
    })();
  }, [sim, compileAndPreview]);

  const handleRun = useCallback(async () => {
    try {
      const result = await compileAndBundle(true);
      if (!result || result.hasErrors) {
        console.error("compile failed: error-level diagnostics present");
        return;
      }
      await sim.init(result.iife);
      sim.setSpeed(speed);
      sim.start();
    } catch (err) {
      console.error("compile failed:", err);
    }
  }, [sim, speed, compileAndBundle]);

  // Embed export dialog. The request counter discards results of a
  // compile/encode that was still in flight when the user closed the dialog
  // (or clicked Embed again), so a stale snapshot can never reopen it.
  const [embedDialog, setEmbedDialog] = useState<EmbedDialogState | null>(null);
  const embedRequestRef = useRef(0);

  const projectTitle = snapshot?.title;
  const handleEmbed = useCallback(async () => {
    const requestId = ++embedRequestRef.current;
    setEmbedDialog({ kind: "loading" });
    try {
      const result = await compileAndBundle(true);
      if (embedRequestRef.current !== requestId) return;
      if (!result) {
        setEmbedDialog({
          kind: "error",
          message: "The editor is still loading — try again in a moment.",
        });
        return;
      }
      if (result.hasErrors) {
        setEmbedDialog({ kind: "compile-error" });
        return;
      }
      const payload: EmbedPayload = {
        v: 1,
        iife: result.iife,
        ...(projectTitle ? { title: projectTitle } : {}),
      };
      const encoded = await encodeEmbedPayload(payload);
      if (embedRequestRef.current !== requestId) return;
      setEmbedDialog({ kind: "ready", payload, encoded });
    } catch (err) {
      if (embedRequestRef.current !== requestId) return;
      setEmbedDialog({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [compileAndBundle, projectTitle]);

  const handleCloseEmbed = useCallback(() => {
    // Invalidate any in-flight build so it cannot reopen the dialog.
    embedRequestRef.current++;
    setEmbedDialog(null);
  }, []);

  const myRole = snapshot?.role;
  const canWrite = myRole === "admin" || myRole === "writer";
  const isReadOnly = !canWrite;

  // Pin the project to the current deployment so viewers open the same app
  // version. The store no-ops for readers and when already pinned here.
  useEffect(() => {
    if (!project || !canWrite || __DEPLOYMENT_ID__ === "local") return;
    void project.pinDeployment({
      id: __DEPLOYMENT_ID__,
      url: __DEPLOYMENT_URL__,
    });
  }, [project, canWrite]);

  // Lazy-bootstrap annotations for projects predating the drawing surface.
  // Readers see drawing disabled until a writer has opened the project once.
  // The store no-ops when the surface already exists, so re-runs are free.
  useEffect(() => {
    if (!project || !canWrite) return;
    void project.ensureAnnotations();
  }, [project, canWrite]);

  // `null` until the bootstrap above has populated the drawing surface on
  // first open (or until a reader is looking at a project no writer has
  // opened yet). The toolbar and overlay are both gated on this so we
  // never render a half-wired surface.
  const annotationsBackend = project?.getAnnotationsBackend() ?? null;

  // The toolbar owns the drawing UI state and pushes the latest snapshot
  // up to us via `onStateChange`. Keeping the state here (rather than
  // threading callbacks through the toolbar) lets us hand the same fields
  // to `DrawOverlay` for both views without a context.
  const [drawState, setDrawState] = useState<DrawingState>({
    tool: "hand",
    color: "#ff5252",
    width: 4,
  });

  // Clear all strokes for whichever view the user is currently looking at.
  // We deliberately scope by the active view so a writer cannot wipe
  // strokes on a view they cannot see; readers never see this control
  // because the toolbar gates it on `canWrite`.
  const handleClearStrokes = useCallback(() => {
    if (!annotationsBackend) return;
    if (markdownSource !== null) {
      void annotationsBackend.clearStrokes({
        view: "markdown",
        ...(activeFile ? { filePath: activeFile } : {}),
      });
    } else {
      void annotationsBackend.clearStrokes({ view: "diagram" });
    }
  }, [annotationsBackend, markdownSource, activeFile]);

  const authorId = currentUser?.id ?? "";
  // Profile name can be undefined on freshly-created accounts before the
  // user has set one. Fall back to a placeholder so cursor labels always
  // show something.
  const authorName = currentUser?.name ?? "Anonymous";

  const pinnedDeploymentId = snapshot?.pinnedDeployment?.id;
  const showVersionBanner = useMemo(() => {
    if (versionBannerDismissed) return false;
    if (!pinnedDeploymentId) return false;
    if (__DEPLOYMENT_ID__ === "local") return false;
    return pinnedDeploymentId !== __DEPLOYMENT_ID__;
  }, [pinnedDeploymentId, versionBannerDismissed]);

  const isPublic = snapshot?.isPublic ?? false;

  const handleTogglePublic = useCallback(
    (makePublic: boolean) => {
      if (!project) return;
      void project.setPublic(makePublic);
    },
    [project],
  );

  const handleFork = useCallback(() => {
    if (!project || forking) return;
    setForking(true);
    void project
      .fork()
      .then((newId) => {
        if (newId) navigate(`/diagram/${newId}`);
      })
      .catch((err: unknown) => {
        console.error("Fork failed:", err);
      })
      .finally(() => {
        setForking(false);
      });
  }, [project, forking, navigate]);

  if (!currentUser) {
    return (
      <div className="loading-container">
        <h2>Initializing Jazz...</h2>
        <p>Please wait while we set up your account.</p>
      </div>
    );
  }

  if (isLoading) {
    const showRescue = localOnlyMode || loadingTimedOut;
    return (
      <div className="loading-container">
        <h2>Loading diagram...</h2>
        {showRescue ? (
          <>
            <p>
              {localOnlyMode
                ? "You're in local-only mode, so shared projects from the cloud can't load. Add a Jazz API key to view this one."
                : "This is taking longer than expected. If this is a shared link, your Jazz API key may be missing or invalid."}
            </p>
            <div className="loading-rescue-actions">
              <button
                type="button"
                className="loading-rescue-primary"
                onClick={() => {
                  window.localStorage.removeItem(JAZZ_LOCAL_ONLY_STORAGE_KEY);
                  window.localStorage.removeItem(JAZZ_API_KEY_STORAGE_KEY);
                  window.location.reload();
                }}
                title="Clear local-only mode and reload to enter a Jazz API key"
              >
                Set up Jazz API key
              </button>
              <button
                type="button"
                className="loading-rescue-secondary"
                onClick={() => {
                  navigate("/projects");
                }}
                title="Return to the project list"
              >
                Back to my projects
              </button>
            </div>
          </>
        ) : (
          <p>Please wait while we load your diagram.</p>
        )}
      </div>
    );
  }

  if (!project || !snapshot || !fileStore) {
    return (
      <div className="error-container">
        <h2>Diagram not found</h2>
        <p>
          The diagram you're looking for doesn't exist or couldn't be loaded.
        </p>
      </div>
    );
  }

  const leadingControls = (
    <button
      type="button"
      className="sim-btn sim-btn-back"
      onClick={() => {
        navigate("/projects");
      }}
      title="Back to all projects"
    >
      {"← All projects"}
    </button>
  );

  // Always available while a project is open, so a problem that never throws
  // — a wrong layout, an implausible simulation result — still has somewhere
  // to go.
  const trailingControls = <ReportIssueLink />;

  const showDiagram = markdownSource === null && enrichedSpec !== null;

  return (
    <div className="workspace-container">
      <SimControls
        leading={leadingControls}
        trailing={trailingControls}
        projectTitle={snapshot.title}
        onTitleChange={
          isReadOnly
            ? undefined
            : (title) => {
                void project.setTitle(title);
              }
        }
        onFork={handleFork}
        onEmbed={() => {
          void handleEmbed();
        }}
        forkDisabled={forking}
        readOnly={isReadOnly}
        isPublic={isPublic}
        onTogglePublic={handleTogglePublic}
        canManageAccess={myRole === "admin"}
      />
      {isReadOnly && (
        <div className="readonly-banner">
          <span>This project is read-only.</span>
          <button
            className="sim-btn sim-btn-primary"
            onClick={handleFork}
            disabled={forking}
            title="Create your own editable copy of this project"
          >
            {forking ? "Forking..." : "Fork to edit"}
          </button>
        </div>
      )}
      {showVersionBanner && (
        <div className="version-mismatch-banner">
          <span>This simulation was last run on a different app version.</span>
          <button
            className="sim-btn sim-btn-primary"
            onClick={() => {
              const url = snapshot.pinnedDeployment?.url;
              if (url && diagramId && url.endsWith(".vercel.app")) {
                const apiKey = localStorage.getItem(JAZZ_API_KEY_STORAGE_KEY);
                const hash = apiKey
                  ? `#apikey=${encodeURIComponent(apiKey)}`
                  : "";
                window.location.href = `https://${url}/diagram/${diagramId}${hash}`;
              }
            }}
            title="Reopen this project on the pinned app deployment"
          >
            Open in pinned version
          </button>
          <button
            className="sim-btn"
            onClick={() => {
              setVersionBannerDismissed(true);
            }}
            title="Dismiss the banner and keep using the current app version"
          >
            Run on current version
          </button>
          <button
            className="sim-btn"
            onClick={() => {
              void project.pinDeployment({
                id: __DEPLOYMENT_ID__,
                url: __DEPLOYMENT_URL__,
              });
              setVersionBannerDismissed(true);
            }}
            title="Pin this project to the current app version"
          >
            Migrate to latest
          </button>
        </div>
      )}
      <div className="split-container">
        <CollapsiblePane
          className="files-pane"
          collapsed={fileTreeCollapsed}
          onToggle={() => {
            setFileTreeCollapsed((prev) => !prev);
          }}
          toggleSide="left"
          collapseLabel="Collapse file tree"
          expandLabel="Expand file tree"
          expandedIcon="◀"
          collapsedIcon="▶"
        >
          <FileTreePanel
            fileStore={fileStore}
            onSelect={handleFileSelect}
            activeFile={activeFile}
            theme="light"
            readOnlyFiles={readOnlyTreeFiles}
            readOnly={isReadOnly}
          />
        </CollapsiblePane>
        <CollapsiblePane
          className="editor-pane-col"
          collapsed={editorCollapsed}
          onToggle={() => {
            setEditorCollapsed((prev) => !prev);
          }}
          toggleSide="right"
          collapseLabel="Collapse editor"
          expandLabel="Expand editor"
          expandedIcon="◀"
          collapsedIcon="▶"
        >
          <EditorComponent
            ref={editorRef}
            fileStore={fileStore}
            collaborativeText={isReadOnly ? undefined : collaborativeText}
            initialFile="/main.ts"
            tsConfig={TS_CONFIG}
            extraLibs={SIM_MODEL_EXTRA_LIBS}
            theme="light"
            className="editor-pane"
            onActiveTabChange={handleActiveTabChange}
            onContentChange={isReadOnly ? undefined : handleContentChange}
            onNavigate={handleNavigate}
            onViewReady={setEditorView}
            readOnly={isReadOnly}
          />
        </CollapsiblePane>
        <div className="split-spacer" aria-hidden="true" />
        <CollapsiblePane
          className="right-pane"
          collapsed={rightPaneCollapsed}
          onToggle={() => {
            setRightPaneCollapsed((prev) => !prev);
          }}
          toggleSide="right"
          collapseLabel="Collapse preview"
          expandLabel="Expand preview"
          expandedIcon="▶"
          collapsedIcon="◀"
        >
          {showDiagram && (
            <SimulationToolbar
              status={sim.status}
              simTime={sim.snapshot?.simTime ?? null}
              error={sim.error}
              speed={speed}
              timeWindow={timeWindow}
              onRun={() => {
                void handleRun();
              }}
              onPause={sim.pause}
              onResume={sim.start}
              onStep={sim.step}
              onReset={handleReset}
              onSetSpeed={(v) => {
                setSpeed(v);
                sim.setSpeed(v);
              }}
              onSetTimeWindow={setTimeWindow}
              handDrawn={
                effectiveAppearanceTheme(
                  appearanceOverride,
                  enrichedSpec.appearance,
                ) === "hand-drawn"
              }
              onToggleHandDrawn={() => {
                setAppearanceOverride(
                  flippedAppearanceOverride(
                    appearanceOverride,
                    enrichedSpec.appearance,
                  ),
                );
              }}
            />
          )}
          {annotationsBackend && (
            <DrawingToolbar
              canWrite={canWrite}
              onClear={handleClearStrokes}
              onStateChange={setDrawState}
            />
          )}
          {markdownSource !== null ? (
            <MarkdownPreview
              source={markdownSource}
              editorView={
                isReadOnly || activeFileIsReadOnly
                  ? undefined
                  : (editorView ?? undefined)
              }
              currentAuthor={currentUser.name}
              renderOverlay={
                annotationsBackend
                  ? (transform) => (
                      <DrawOverlay
                        backend={annotationsBackend}
                        transform={transform}
                        mode="markdown"
                        {...(activeFile ? { filePath: activeFile } : {})}
                        enabled={true}
                        tool={drawState.tool}
                        color={drawState.color}
                        width={drawState.width}
                        authorId={authorId}
                        authorName={authorName}
                      />
                    )
                  : undefined
              }
            />
          ) : enrichedSpec ? (
            <DiagramRenderer
              spec={enrichedSpec}
              nodeTypes={NODE_TYPES}
              className="diagram-pane"
              colorMode="light"
              {...(appearanceOverride
                ? { appearance: { theme: appearanceOverride } }
                : {})}
              renderOverlay={
                annotationsBackend
                  ? (transform) => (
                      <DrawOverlay
                        backend={annotationsBackend}
                        transform={transform}
                        mode="diagram"
                        enabled={true}
                        tool={drawState.tool}
                        color={drawState.color}
                        width={drawState.width}
                        authorId={authorId}
                        authorName={authorName}
                      />
                    )
                  : undefined
              }
            />
          ) : sim.previewError ? (
            <div className="preview-error">
              <p>{sim.previewError}</p>
            </div>
          ) : (
            <div className="preview-placeholder">
              <p>Write simulation code to see the diagram</p>
            </div>
          )}
        </CollapsiblePane>
      </div>
      {embedDialog && (
        <EmbedDialog state={embedDialog} onClose={handleCloseEmbed} />
      )}
    </div>
  );
};

export default DiagramWorkspace;
